/**
 * Meneer AI Module
 * Groq-powered study assistant — the single AI entry point for the app.
 * To switch to a backend later: replace callMeneer() with a fetch to your server.
 * Exposed as window.Meneer
 */

const MENEER_CONFIG = {
    // Gemini (tool calls only — preserves 20 RPD for actions, chat routes to Groq)
    // gemini-2.5-flash-lite: 10 RPM / 250K TPM / 20 RPD — confirmed on OpenAI-compatible endpoint
    // gemini-2.5-flash:       5 RPM / 250K TPM / 20 RPD — fallback on 429
    // NOTE: gemini-3.1-flash-lite has 500 RPD in Studio but is NOT yet on the OpenAI-compatible endpoint (returns 400)
    // TOOL CALLS USE FLASH, NOT FLASH-LITE, and that is deliberate.
    //
    // flash-lite was the tool-calling model and it does not reliably emit
    // structured tool_calls through the OpenAI-compatible endpoint. Observed in
    // production 2026-07-28: asked to build a quiz, it returned
    //
    //     tool_code print(default_api.quiz_action(action='generate', topic='PHY1013S'))
    //
    // as message CONTENT — the right tool, the right arguments, in Gemini's own
    // native representation rather than as a function call. So the schemas were
    // reaching the model and it knew exactly what to do; the response shape was
    // the only thing wrong, and nothing ever executed.
    //
    // flash costs more RPM (5 vs 10) but a tool call that never runs is worth
    // nothing at any price.
    MODEL_GEMINI:          'gemini-2.5-flash',
    MODEL_GEMINI_LITE:     'gemini-2.5-flash-lite',   // still fine for plain chat
    MODEL_GEMINI_FALLBACK: 'gemini-2.5-flash-lite',

    // Groq (plain chat — 14,400 RPD on llama-8b, preserves Gemini quota for tool calls)
    MODEL:      'llama-3.3-70b-versatile', // tool calls fallback
    // Was 'llama-3.1-8b-instant', which Groq no longer serves to us (QA AI-1). Only
    // the 'groq' and 'split' providers still use this; 'gemini' routes chat to Gemini.
    MODEL_FAST: 'llama-3.3-70b-versatile',    // chat

    // OpenAI (paid credits — added 2026-08-06 to evaluate Meneer on OpenAI models).
    // Unlike the free tiers above there is no meaningful RPD wall here; the limit
    // is the credit balance, so there is no chat/tools quota split to preserve and
    // the two models below are chosen for capability, not for rationing.
    //
    // Tool calls use the full model for the same reason Gemini does: the cheaper
    // model is the one that gets structured tool_calls wrong, and a tool call that
    // never runs is worth nothing at any price.
    MODEL_OPENAI:        'gpt-4.1',
    MODEL_OPENAI_FAST:   'gpt-4.1-mini',   // chat
    // Every gpt-4.1 model is natively multimodal, so vision needs no separate
    // model of its own.
    MODEL_OPENAI_VISION: 'gpt-4.1-mini',
    // Vision for every provider that is not OpenAI — see _getVisionModel().
    MODEL_GEMINI_VISION: 'gemini-2.5-flash',

    /**
     * HOW MUCH ROOM MENEER GETS TO ANSWER A STUDENT. One number, every surface.
     *
     * It was four numbers before, chosen per call site: the Meneer screen asked
     * for 4000 while the PDF reader's assistant asked for 600, so the same
     * question got nearly seven times less room depending on which screen a
     * student happened to be on. Reported from production 2026-08-19 — a worked
     * determinant question stopped mid-expression at `\det = 3 \times (-1)^{`.
     *
     * MATHS IS WHY THE CEILING BINDS. LaTeX is extraordinarily token-dense:
     * `\begin{vmatrix}`, `\times` and `M_{21}` are several tokens each and carry
     * almost no words, so a worked solution spends its budget many times faster
     * than the same explanation in prose. The surface where students read their
     * maths was the one least able to explain it.
     *
     * This is a CEILING, not a cost. The AI quota meters tokens actually
     * produced, so raising it charges nothing for the short answers that are
     * most answers — it only stops cutting off the long ones.
     */
    ANSWER_MAX_TOKENS: 4000,

    /**
     * Budget for STRUCTURED output, where the shape bounds the length and a
     * bigger ceiling buys nothing — currently the PDF table-of-contents
     * extractor, which returns a JSON array of headings.
     *
     * Kept separate from ANSWER_MAX_TOKENS on purpose: these two numbers move
     * for entirely different reasons, and collapsing them would tie a JSON
     * budget to how long a student's maths answer needs to be.
     */
    STRUCTURE_MAX_TOKENS: 800,

    /**
     * How much gemini-2.5-flash may THINK before it answers — paid ON TOP of the
     * answer budget, never out of it.
     *
     * Flash thinks by default, with no cap, and its thinking is charged to the
     * same max_tokens as the answer. Reported from a tablet 2026-10-06: a
     * Biot-Savart question stopped at `$\mu_0 = 4`, and "continue" stopped again
     * at `\text{m` — with ANSWER_MAX_TOKENS already 4000. The reasoning had spent
     * the budget; the student saw about 250 tokens of it.
     *
     * 1024 is what reasoning_effort 'low' maps to on Google's OpenAI-compatible
     * endpoint. See _budgetFor().
     */
    GEMINI_THINKING_TOKENS: 1024,

    /**
     * Appended to a PROSE answer whose finish_reason was 'length'. A cut-off used
     * to be silent, so it read as Meneer finishing mid-sentence. Never appended
     * to structured output (JSON, note HTML) — see _markIfCut().
     */
    CUT_OFF_NOTE: "*I ran out of room before finishing. Ask me to continue and I'll pick up where I stopped.*",
};

/**
 * Models that think by default. flash-lite does NOT, and sending it
 * reasoning_effort 'low' would turn thinking ON — so this is an exact list, not
 * a prefix match on 'gemini'.
 */
const _THINKING_MODELS = new Set(['gemini-2.5-flash']);

/**
 * The token fields of a request body for `model`, given the room the ANSWER
 * needs. Computed per attempt, because the retry loops fall back from flash to
 * flash-lite and the two need different fields.
 */
function _budgetFor(model, answerTokens) {
    if (!_THINKING_MODELS.has(model)) return { max_tokens: answerTokens };
    return {
        max_tokens:       answerTokens + MENEER_CONFIG.GEMINI_THINKING_TOKENS,
        reasoning_effort: 'low',
    };
}

/**
 * Name a cut-off in a prose answer. Closes an unterminated code fence first, or
 * the note would render as code inside it.
 */
function _markIfCut(text, finishReason) {
    if (!text || finishReason !== 'length') return text;
    console.warn('[Meneer] answer hit max_tokens (finish_reason=length)');
    const fences = (text.match(/```/g) || []).length;
    return `${text}${fences % 2 ? '\n```' : ''}\n\n${MENEER_CONFIG.CUT_OFF_NOTE}`;
}

/** Returns the right model based on active provider. Both chat and tools use the same Gemini model. */
/**
 * Which model answers, for a given kind of work.
 *
 * Two bugs used to live in this function.
 *
 * 1. Provider was inferred from `if (APP_CONFIG.GEMINI_API_KEY)` — so moving the
 *    key server-side, where it belongs, would have silently demoted everyone to
 *    Groq. Provider is a deployment decision now: AI_PROVIDER.
 *
 * 2. `forTools` was never read. The early return fired first, so BOTH call sites
 *    — _getModel(true) for tool calls and _getModel(false) for chat — got the same
 *    model. That silently collapsed the split MENEER_CONFIG still documents:
 *    scarce Gemini per-day quota reserved for tool calls, abundant Groq quota
 *    absorbing chat volume. The design was written down and unreachable.
 *
 * 'gemini' is the default because it is the release provider. 'split' is the more
 * durable option and is now actually selectable — see
 * docs/specs/2026-07-27-multi-provider-routing.md for why it should eventually
 * move server-side into ai-proxy.
 */
function _getModel(forTools = false) {
    const provider = window.APP_CONFIG?.AI_PROVIDER || 'gemini';

    if (provider === 'split') {
        // The documented design: tool calls get Gemini, chat goes to Groq.
        return forTools ? MENEER_CONFIG.MODEL_GEMINI : MENEER_CONFIG.MODEL_FAST;
    }
    if (provider === 'groq') {
        return forTools ? MENEER_CONFIG.MODEL : MENEER_CONFIG.MODEL_FAST;
    }
    if (provider === 'openai') {
        return forTools ? MENEER_CONFIG.MODEL_OPENAI : MENEER_CONFIG.MODEL_OPENAI_FAST;
    }
    // 'gemini' — one provider for everything. Simplest, and correct on a paid
    // tier; on the free tier its per-day cap applies to the whole app, not per user.
    return MENEER_CONFIG.MODEL_GEMINI;
}

/**
 * Resolve a call site that pins a specific model.
 *
 * Two call sites name a model directly instead of asking _getModel:
 * meneer-chat.js pins Groq's 8b for plain chat, meneer-screen.js pins Gemini
 * flash for its tool loop. Both pins are deliberate and both exist for the same
 * reason — to ration a scarce free-tier daily quota that is shared by the whole
 * app, not to express a preference for those models.
 *
 * A pin is therefore only meaningful while the active provider HAS such a quota.
 * OpenAI does not: it is prepaid credit with no per-day ceiling, so there is
 * nothing to ration and the pin has no purpose left. Honouring it there would
 * have kept the chat screen on Groq and the Meneer screen on Gemini for the
 * whole of an OpenAI evaluation — the two busiest surfaces in the app, silently
 * measuring the wrong provider.
 *
 * Every other provider keeps its pin exactly as before, so the release
 * configuration is unchanged.
 */
function _resolvePinnedModel(pinnedModel, forTools = false) {
    const provider = window.APP_CONFIG?.AI_PROVIDER || 'gemini';
    if (provider === 'openai') return _getModel(forTools);

    // A pin to ANOTHER provider's model is only valid if that provider is in use.
    // Plain chat was pinned to Groq's llama-3.1-8b-instant even when AI_PROVIDER
    // was 'gemini', and when Groq stopped serving that model every plain-chat
    // turn in the floating chat failed with "The model ... does not exist"
    // (QA AI-1, 2026-10-02): six of ten questions answered, four dead. Under
    // 'gemini' there is no Groq to ration against, so use Gemini for chat too.
    if (provider === 'gemini' && _providerOf(pinnedModel) !== 'gemini') {
        return forTools ? MENEER_CONFIG.MODEL_GEMINI : MENEER_CONFIG.MODEL_GEMINI_LITE;
    }
    return pinnedModel;
}

/**
 * Which provider owns a model name.
 *
 * The model name is the only routing signal the whole system has — the client
 * uses it to pick a key, and ai-proxy uses it to pick an upstream URL. THE TWO
 * MUST AGREE. If they drift, a model resolves to one provider's endpoint with
 * another provider's key and comes back 401, which reads like an outage.
 * `tests/meneer-routing.test.js` pins the mapping; the mirror lives in
 * `supabase/functions/ai-proxy/index.ts`.
 *
 * Groq is last and unprefixed because its model names have no shared prefix
 * (`llama-…`, `meta-llama/…`), so it is the default rather than a match.
 */
function _providerOf(model = '') {
    if (model.startsWith('gemini')) return 'gemini';
    if (model.startsWith('gpt-'))   return 'openai';
    return 'groq';
}

/**
 * Which model answers a vision request.
 *
 * Vision was hard-wired to Llama 4 Scout, which meant the PDF reader silently
 * stayed on Groq no matter what AI_PROVIDER said — so a provider evaluation
 * would have quietly excluded the one surface that sends images.
 *
 * EVERY OTHER PROVIDER GOES TO GEMINI, including 'groq'. Groq shut Llama 4
 * Scout down on 2026-07-17 and its recommended replacements are text-only, so
 * Groq has no vision model to offer. Nothing noticed for over two months
 * because production was on OpenAI; switching back to Gemini (2026-09-27, out
 * of OpenAI credits) sent every PDF question to a retired model, and the
 * text-only fallback answered "I can't see the page" — reported from production
 * 2026-09-28. A dead model here does not look like an error. It looks like
 * Meneer being unhelpful.
 */
function _getVisionModel() {
    const provider = window.APP_CONFIG?.AI_PROVIDER || 'gemini';
    if (provider === 'openai') return MENEER_CONFIG.MODEL_OPENAI_VISION;
    return MENEER_CONFIG.MODEL_GEMINI_VISION;
}

/**
 * Format a structured quota error from ai-proxy into a human-readable message.
 *
 * The proxy returns { code, message, reset_at } — see 002_ai_quota.sql.
 * We turn reset_at into a local time string so the student knows when they
 * can try again, rather than seeing a raw ISO timestamp.
 */
function _formatQuotaError(body) {
    let resetStr = '';
    if (body?.reset_at) {
        try {
            const d = new Date(body.reset_at);
            resetStr = ` It resets at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`;
        } catch (_) { /* invalid date — skip */ }
    }
    return (body?.message || 'Your daily AI limit has been reached.') + resetStr;
}

/**
 * Returns the correct API URL for a given model.
 * Gemini models → Gemini OpenAI-compatible endpoint.
 * Groq/Llama models → Supabase proxy or direct Groq.
 * Always pass the model so routing is correct even when both keys are set.
 */
function _getApiUrl(model = '') {
    const base = window.APP_CONFIG?.SUPABASE_URL;

    // EVERY provider goes through the one proxy. Gemini used to be exempted here
    // and called Google directly with a key out of window.APP_CONFIG — which is
    // readable by any visitor, so the key was spendable by anyone who opened
    // devtools, and the calls never reached our server so could not be metered.
    if (base) return `${base}/functions/v1/ai-proxy`;

    // Direct provider calls only in a deployment with no Supabase at all.
    switch (_providerOf(model)) {
        case 'gemini':
            return 'https://generativelanguage.googleapis.com/v1beta/openai/chat/completions';
        case 'openai':
            return 'https://api.openai.com/v1/chat/completions';
        default:
            return 'https://api.groq.com/openai/v1/chat/completions';
    }
}

/**
 * Returns the correct Authorization header for a given model.
 */
async function _getAuthHeader(model = '') {
    // When the proxy is in play, the session token is the ONLY correct credential,
    // for every provider. A raw provider key sent here would be both a leak and
    // wrong: ai-proxy validates a Supabase JWT and would answer 401, which reads
    // like a provider outage and is miserable to debug.
    if (window.APP_CONFIG?.SUPABASE_URL) {
        if (window.SupabaseClient) {
            const { data: { session } } = await window.SupabaseClient.auth.getSession();
            if (session) return `Bearer ${session.access_token}`;
        }
        throw new Error('Not authenticated. Please sign in to use AI features.');
    }

    // Below here: no Supabase configured, so calls go straight to the provider.
    // Client-held keys are a last resort for that setup, never a design — anything
    // in window.APP_CONFIG is readable by any user (AGENTS.md SKILL 4).
    const provider = _providerOf(model);
    if (provider === 'gemini') {
        const key = window.APP_CONFIG?.GEMINI_API_KEY;
        if (key) return `Bearer ${key}`;
        throw new Error('Gemini API key not set, and no Supabase proxy configured.');
    }
    if (provider === 'openai') {
        const key = window.APP_CONFIG?.OPENAI_API_KEY;
        if (key) return `Bearer ${key}`;
        throw new Error('OpenAI API key not set, and no Supabase proxy configured.');
    }
    const key = window.APP_CONFIG?.GROQ_API_KEY;
    if (key) return `Bearer ${key}`;
    throw new Error('Not authenticated. Please sign in to use AI features.');
}

// ── Personality constants ──────────────────────────────────────────────────

const CHAT_FORMATTING_RULES =
    '\n\nFORMATTING RULES FOR CHAT MESSAGES:\n' +
    '- Always format your responses using clean Markdown (e.g., ## for section titles, **bold** for emphasis, - for bullet lists, 1. for numbered lists, | for tables, ``` for code blocks).\n' +
    '- MATHEMATICAL & SCIENTIFIC FORMULAS: Use LaTeX ($...$ for inline equations, $$...$$ for block equations) ONLY for mathematical, scientific, engineering, and chemical notation (e.g. $E = mc^2$, $v_C(t)$, $x[n] = x(nT)$, $\\ce{H2SO4}$, $$\\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$$). Do not wrap ordinary words or plain text in dollar signs.\n' +
    '- NEVER output raw HTML tags (like <h2>, <h3>, <ul>, <ol>, <li>, <table>, <tr>, <th>, <td>, <strong>) in conversation text messages.\n' +
    '- HTML tags are ONLY used as parameter values when calling note-editing tools (like create_note or edit_note).';


const CHARACTER_PROMPT =
`You are Meneer, a passionate and slightly dramatic study coach who genuinely cares about the student's success. You have high standards and push students to think deeper. You're enthusiastic about learning, occasionally a bit theatrical ("This is CRUCIAL!"), and you remember that studying is hard — so you balance high expectations with real encouragement. You speak like a knowledgeable mentor who is also a bit of a character. Keep replies concise and clear unless the student asks for depth.${CHAT_FORMATTING_RULES}`;

const STANDARD_PROMPT =
`You are a helpful AI study assistant. Answer questions clearly and concisely. When helping with tasks or notes, be accurate and direct. Use plain language and avoid unnecessary filler.${CHAT_FORMATTING_RULES}`;


const LANGUAGE_MODULES = {
    nl: `Always reply in Dutch (Nederlands). Use natural, fluent Dutch — not a word-for-word translation. Keep the same tone and meaning.`,
    fr: `Always reply in French (Français). Use natural, fluent French. Keep the same tone and meaning.`,
    de: `Always reply in German (Deutsch). Use natural, fluent German. Keep the same tone and meaning.`,
    es: `Always reply in Spanish (Español). Use natural, fluent Spanish. Keep the same tone and meaning.`,
    ar: `Always reply in Arabic (العربية). Use natural, fluent Arabic. Keep the same tone and meaning.`
};

/**
 * Returns the correct system prompt based on user's personality + language settings.
 * @param {string} [context] - Optional extra context appended after the base prompt.
 * @returns {string}
 */
function getMeneerSystemPrompt(context = '') {
    const mode = localStorage.getItem('meneer_mode') || 'meneer';
    const lang = localStorage.getItem('meneer_language') || 'en';
    let prompt = mode === 'standard' ? STANDARD_PROMPT : CHARACTER_PROMPT;
    if (lang !== 'en' && LANGUAGE_MODULES[lang]) {
        prompt += '\n\n' + LANGUAGE_MODULES[lang];
    }

    // ── Behaviour settings ────────────────────────────────────────────────────
    const cfg = window.Storage ? Storage.loadSettings() : {};

    if (cfg.meneerProactive !== false) {
        prompt += '\n\nWhen it is relevant, proactively suggest actions to the student: ' +
            'offer to create flashcards from key concepts, summarise a section, ' +
            'or quiz them on recently covered material.';
    }

    if (cfg.meneerShowReasoning) {
        prompt += '\n\nThink through your answer step by step before giving your final response. ' +
            'Show your reasoning briefly so the student can follow your logic.';
    }

    if (cfg.meneerUseNotes !== false) {
        // Inject a brief reference to the student's recent notes so Meneer can
        // tie answers back to material the student is actually studying.
        try {
            const notes = Storage.loadNotes();
            if (notes && notes.length > 0) {
                const titles = notes.slice(0, 5)
                    .map(n => n.title || 'Untitled')
                    .join(', ');
                prompt += `\n\nThe student has notes on: ${titles}. ` +
                    'Reference these when it helps to connect your answer to their own material.';
            }
        } catch (_) { /* gracefully skip if storage not available */ }
    }

    if (context) prompt += '\n\n' + context;
    return prompt;
}

/**
 * Strip HTML tags to get plain text for AI prompts
 */
function stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
}

/**
 * Core Meneer API call.
 * Routes through Supabase Edge Function groq-proxy (API key stays server-side).
 *
 * @param {string} systemPrompt - Meneer's instructions
 * @param {string} userMessage  - The student's input / content to analyse
 * @param {number} maxTokens    - Max response length (default 100)
 * @returns {Promise<string|null>} AI response text, or null on any failure
 */
// A short id per AI request. It goes to ai-proxy as `?rid=` (a query parameter,
// not a header: a custom header would need ai-proxy's CORS allow-list updated
// first, and shipping the client ahead of that deploy would block every AI call)
// and is printed here, so one console line and one proxy log line can be matched
// exactly instead of by time (QA AI-5).
function _newRequestId() {
    return Math.random().toString(36).slice(2, 8);
}

function _tracked(url, model) {
    const id = _newRequestId();
    return { id, model, url: `${url}${url.includes('?') ? '&' : '?'}rid=${id}` };
}

function _logRequest(rq, status) {
    console.info(`[Meneer] req=${rq.id} model=${rq.model} -> HTTP ${status}`);
}

// Why the last callMeneer() returned null. callMeneer collapses every failure to
// null so non-chat callers can degrade quietly, which meant the quiz generator
// could only say "confirm you are signed in" for a 503 as much as for a sign-in
// problem (QA AI-5). Callers that want an honest message read this.
let _lastCallFailure = null;

function getLastCallFailure() { return _lastCallFailure; }

/**
 * A student-facing sentence for a recorded failure. `kind` is one of
 * auth | limit | busy | offline | error.
 */
function describeAIFailure(reason) {
    switch (reason && reason.kind) {
        case 'auth':    return 'Your session has expired. Please sign in again, then try again.';
        case 'limit':   return "You've reached your AI limit for now. Try again later.";
        case 'busy':    return 'The AI is busy right now. Try again in a moment.';
        case 'offline': return 'No internet connection — check your network and try again.';
        default:        return "The AI couldn't do that just now. Please try again.";
    }
}

function _failureKind(status) {
    if (status === 401 || status === 403) return 'auth';
    if (status === 429) return 'limit';
    if (status >= 500) return 'busy';
    return 'error';
}

async function callMeneer(systemPrompt, userMessage, maxTokens = 100) {
    _lastCallFailure = null;
    const model      = _getModel(false);
    const authHeader = await _getAuthHeader(model);

    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 30000);
    const rq = _tracked(_getApiUrl(model), model);

    try {
        const response = await fetch(rq.url, {
            method: 'POST',
            signal: controller.signal,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader
            },
            body: JSON.stringify({
                model,
                messages: [
                    { role: 'system', content: systemPrompt },
                    { role: 'user',   content: userMessage  }
                ],
                ..._budgetFor(model, maxTokens),
                temperature: 0.3
            })
        });

        clearTimeout(timeoutId);
        _logRequest(rq, response.status);

        if (!response.ok) {
            let detail = `(HTTP ${response.status})`;
            try {
                const errBody = await response.json();
                detail = errBody?.error?.message || detail;
            } catch (_) {}
            console.warn('Meneer: API error —', detail);
            _lastCallFailure = { kind: _failureKind(response.status), status: response.status, message: detail };
            return null; // non-chat callers (note structuring etc.) handle null gracefully
        }

        const data = await response.json();
        const text = data?.choices?.[0]?.message?.content;
        // No _markIfCut: every caller of callMeneer parses structured output.
        if (data?.choices?.[0]?.finish_reason === 'length') console.warn('[Meneer] callMeneer hit max_tokens');
        return text ? text.trim() : null;

    } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
            console.warn('Meneer: request timed out');
            _lastCallFailure = { kind: 'busy', timeout: true, message: 'timed out' };
            return null;
        }
        console.warn('Meneer: network error —', err.message);
        _lastCallFailure = { kind: 'offline', message: err.message };
        return null;
    }
}

/**
 * Suggest a short category name for a note.
 *
 * @param {string} title   - Note title (plain text)
 * @param {string} content - Note content (HTML — stripped internally)
 * @returns {Promise<string|null>} Short category string, or null on failure
 */
async function suggestNoteCategory(title, content) {
    const plain = stripHtml(content);
    const system = getMeneerSystemPrompt(
        'The student wants you to categorise a note. Respond with ONLY a short category name ' +
        '(2–4 words max, no punctuation, no explanation, no quotes). ' +
        'Examples: Java Programming, Mathematics, History, Personal, Biology, Web Development.'
    );
    const user = `Title: ${title}\n\nContent: ${plain.substring(0, 800)}`;

    const result = await callMeneer(system, user, 20);
    if (!result) return null;

    // Sanitise: strip stray quotes, newlines, extra whitespace
    return result.replace(/["""''`\n\r]/g, '').trim().substring(0, 50) || null;
}

/**
 * Vision-capable call — the model comes from _getVisionModel().
 * Accepts a messages array where the user content may include image_url parts.
 * Used ONLY by the PDF reader AI panel. Do not use for other Meneer features.
 */
async function callMeneerVision(messages, maxTokens = 600) {
    const VISION_MODEL = _getVisionModel();
    // Vision follows AI_PROVIDER: OpenAI when that is selected, otherwise Gemini.
    //
    // This used to call api.groq.com DIRECTLY with window.APP_CONFIG.GROQ_API_KEY,
    // preferring the raw key over the session token. That made the key not merely
    // exposed but demonstrably usable: anything in APP_CONFIG is readable by any
    // visitor, and this request showed exactly how to spend it. It also meant
    // vision usage never touched our server, so it could not be metered at all.
    //
    // groq-proxy needed no changes to accept this — it is a pass-through to the
    // same /chat/completions endpoint, so a multimodal messages array and a vision
    // model go through unaltered, now behind a Supabase JWT.
    const visionUrl = _getApiUrl(VISION_MODEL);
    const visionAuth = await _getAuthHeader(VISION_MODEL);
    try {
        const rq = _tracked(visionUrl, VISION_MODEL);
        const response = await fetch(rq.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': visionAuth
            },
            body: JSON.stringify({
                model: VISION_MODEL,
                messages,
                ..._budgetFor(VISION_MODEL, maxTokens),
                temperature: 0.4
            })
        });
        _logRequest(rq, response.status);
        if (!response.ok) {
            // Returning null hands the question to the text-only fallback, which
            // still answers — so without this line a vision outage is invisible.
            // A retired model went unnoticed for two months exactly this way.
            let detail = '';
            try { detail = (await response.text()).slice(0, 300); } catch (_) {}
            console.warn(`Meneer vision failed — HTTP ${response.status} from ${VISION_MODEL}:`, detail);
            return null;
        }
        const data = await response.json();
        const text = data?.choices?.[0]?.message?.content;
        if (!text) console.warn(`Meneer vision returned no content from ${VISION_MODEL}`);
        return text ? _markIfCut(text.trim(), data.choices[0].finish_reason) : null;
    } catch (err) {
        console.warn('Meneer vision error —', err.message);
        return null;
    }
}

/**
 * Core multi-turn chat call — accepts a full messages array.
 * Used by chatWithNote to support conversation history.
 * Throws on API key / network errors so callers can surface a helpful message.
 */
/**
 * @param {object}  [opts]
 * @param {boolean} [opts.prose] The reply is read by a student, so a cut-off is
 *        named (_markIfCut). Off by default: the TOC extractor parses JSON from
 *        this function, and a sentence appended to it would break the parse.
 */
async function callMeneerWithHistory(messages, maxTokens = 400, model = _getModel(false), opts = {}) {
    const authHeader = await _getAuthHeader(model);
    // Fallback model pool: Gemini models get Gemini fallback, Groq models stay on Groq
    const isGemini  = model.startsWith('gemini');
    const modelPool = isGemini ? [model, MENEER_CONFIG.MODEL_GEMINI_FALLBACK] : [model];
    let   modelIdx  = 0;

    for (let attempt = 0; attempt <= 3; attempt++) {
        if (attempt > 0) {
            // Promote to fallback model on attempt 1 (Gemini only)
            if (isGemini && attempt === 1 && modelIdx < modelPool.length - 1) modelIdx++;
            // Jitter backoff: 2s → 4s → 8s ± 25% — prevents thundering herd on shared quota
            const base  = 2000 * Math.pow(2, attempt - 1);
            const delay = base * (0.75 + Math.random() * 0.5);
            console.warn(`[Meneer] 429 — model: ${modelPool[modelIdx]}, retrying in ${Math.round(delay / 1000)}s`);
            await new Promise(r => setTimeout(r, delay));
        }

        const controller = new AbortController();
        const timeoutId  = setTimeout(() => controller.abort(), 30000);

        try {
            const rq = _tracked(_getApiUrl(modelPool[modelIdx]), modelPool[modelIdx]);
            const response = await fetch(rq.url, {
                method: 'POST',
                signal: controller.signal,
                headers: { 'Content-Type': 'application/json', 'Authorization': authHeader },
                body: JSON.stringify({
                    model:       modelPool[modelIdx],
                    messages,
                    ..._budgetFor(modelPool[modelIdx], maxTokens),
                    temperature: 0.5
                })
            });

            clearTimeout(timeoutId);
            _logRequest(rq, response.status);

            if (response.status === 429) {
                let quotaBody = null;
                try { quotaBody = await response.json(); } catch (_) {}
                // Quota-exceeded from ai-proxy: do NOT retry, surface to user.
                if (quotaBody?.code === 'DAILY_QUOTA_EXCEEDED') {
                    const quotaErr = new Error(_formatQuotaError(quotaBody));
                    // The CODE travels with the error, not just the prose.
                    // classifyError (meneer-actions.js) keys on it to decide that
                    // this is the one failure where "Try again" must NOT be
                    // offered, because retrying cannot succeed. Matching the
                    // message text instead would break the moment the copy
                    // changes — and that message is user-facing copy that will.
                    quotaErr.code = 'DAILY_QUOTA_EXCEEDED';
                    throw quotaErr;
                }
                continue; // transient upstream 429 — retry with jitter + possible model switch
            }

            if (!response.ok) {
                let detail = `(HTTP ${response.status})`;
                try { const b = await response.json(); detail = b?.error?.message || detail; } catch (_) {}
                if (response.status === 401) throw new Error('Invalid API key. Check your API key in config.js.');
                if (response.status === 503) throw new Error('AI service is temporarily unavailable. Try again shortly.');
                throw new Error(`AI returned an error: ${detail}`);
            }

            const data = await response.json();
            const text = data?.choices?.[0]?.message?.content;
            if (!text) return null;
            return opts?.prose ? _markIfCut(text.trim(), data.choices[0].finish_reason) : text.trim();

        } catch (err) {
            clearTimeout(timeoutId);
            if (err.name === 'AbortError') throw new Error('Request timed out after 30 seconds. Check your internet connection.');
            throw err;
        }
    }
    throw new Error('Rate limit — too many requests. Please wait a minute and try again.');
}

/**
 * Function-calling Groq call — sends tools alongside messages.
 * Returns { content, toolCalls, rawMessage } where:
 *   content   — text response (null if the model only called tools)
 *   toolCalls — [{ id, name, args }] or null
 *   rawMessage — the raw assistant message object
 *
 * NOTE: The Supabase Edge Function groq-proxy must pass through the `tools`
 * and `tool_choice` fields unchanged. If it strips unknown fields, function
 * calling will silently fall back to text-only responses (chat still works,
 * but Meneer won't take actions). Update the proxy to forward all body fields.
 */
/**
 * @param {object}      [opts]
 * @param {AbortSignal} [opts.signal] The CALLER's cancel, for a student who no
 *        longer wants to wait. COMPOSED with the internal 35s timeout rather
 *        than replacing it: a caller who forgets to abort must not be able to
 *        remove the bound that protects every other caller. An abort raised
 *        through this signal throws an Error carrying `code = 'STOPPED'`, so it
 *        can be told apart from a timeout the student did not ask for.
 */
async function callMeneerWithTools(messages, tools, maxTokens = 900, model = _getModel(true), opts = {}) {
    const authHeader = await _getAuthHeader(model);
    const hasTools   = tools && tools.length > 0;
    const external   = (opts && opts.signal) || null;

    // ── Rate-limit retry helper ────────────────────────────────────────────────
    // On Gemini: auto-switches to fallback model on first 429, then applies
    // jitter backoff (2s → 4s → 8s ± 25%). No Retry-After — Gemini doesn't send it.
    const _fetchWithRetry = async (body, maxRetries = 3) => {
        const isGemini  = body.model.startsWith('gemini');
        const modelPool = isGemini
            ? [body.model, MENEER_CONFIG.MODEL_GEMINI_FALLBACK]
            : [body.model];
        let modelIdx = 0;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            if (attempt > 0) {
                // Promote to fallback model on attempt 1 (Gemini only)
                if (isGemini && attempt === 1 && modelIdx < modelPool.length - 1) modelIdx++;
                // Jitter backoff: 2s → 4s → 8s ± 25%
                const base  = 2000 * Math.pow(2, attempt - 1);
                const delay = base * (0.75 + Math.random() * 0.5);
                console.warn(`[Meneer] 429 — model: ${modelPool[modelIdx]}, retrying in ${Math.round(delay / 1000)}s`);
                await new Promise(r => setTimeout(r, delay));
            }
            const currentModel = modelPool[modelIdx];
            const controller = new AbortController();
            const timeoutId  = setTimeout(() => controller.abort(), 35000);

            // AbortSignal.any() is not available in every browser this app
            // supports, so the caller's signal and the timeout are composed by
            // hand. `once` plus the explicit removals below keep a listener from
            // outliving the attempt that registered it — this loop runs up to
            // four times per call.
            const onExternalAbort = () => controller.abort();
            if (external) {
                if (external.aborted) controller.abort();
                else external.addEventListener('abort', onExternalAbort, { once: true });
            }

            try {
                const rq = _tracked(_getApiUrl(currentModel), currentModel);
                const resp = await fetch(rq.url, {
                    method:  'POST',
                    signal:  controller.signal,
                    headers: { 'Content-Type': 'application/json', 'Authorization': await _getAuthHeader(currentModel) },
                    // body.max_tokens is the ANSWER budget; the per-model fields
                    // are derived here because a 429 swaps flash for flash-lite.
                    body:    JSON.stringify({ ...body, model: currentModel, ..._budgetFor(currentModel, body.max_tokens) })
                });
                clearTimeout(timeoutId);
                if (external) external.removeEventListener('abort', onExternalAbort);
                _logRequest(rq, resp.status);
                if (resp.status === 429) {
                    let quotaBody = null;
                    try { quotaBody = await resp.json(); } catch (_) {}
                    // Quota-exceeded from ai-proxy: do NOT retry, surface to user.
                    if (quotaBody?.code === 'DAILY_QUOTA_EXCEEDED') {
                        // Same contract as the other 429 site above: the code
                        // travels with the error so classifyError can suppress
                        // the retry without reading user-facing prose.
                        const quotaErr = new Error(_formatQuotaError(quotaBody));
                        quotaErr.code = 'DAILY_QUOTA_EXCEEDED';
                        throw quotaErr;
                    }
                    continue; // transient upstream 429 — retry
                }
                return resp; // success or non-rate-limit error
            } catch (err) {
                clearTimeout(timeoutId);
                if (external) external.removeEventListener('abort', onExternalAbort);
                if (err.name === 'AbortError') {
                    // WHICH abort fired changes what the student is told. A stop
                    // they asked for is not a failure, and reporting it as a
                    // timeout blames the connection for their own decision.
                    if (external && external.aborted) {
                        const stopped = new Error('Stopped waiting for a reply.');
                        stopped.code = 'STOPPED';
                        throw stopped;
                    }
                    throw new Error('Request timed out after 35 seconds. Check your internet connection.');
                }
                throw err;
            }
        }
        throw new Error('Rate limit reached — please try again in a minute.');
    };

    try {
        let response = await _fetchWithRetry({
            model,
            messages,
            tools:        hasTools ? tools : undefined,
            tool_choice:  hasTools ? 'auto' : undefined,
            max_tokens:   maxTokens,
            temperature:  0
        });

        // ── failed_generation fallback ──────────────────────────────────────
        if (!response.ok && response.status === 400) {
            let errBody = {};
            try { errBody = await response.json(); } catch (_) {}
            const errMsg = errBody?.error?.message || '';
            if (errMsg.includes('failed_generation') || errBody?.error?.code === 'failed_generation') {
                console.warn('[Meneer] failed_generation — retrying without tools');
                const fallback = await _fetchWithRetry({
                    model:       model,
                    messages,
                    max_tokens:  maxTokens,
                    temperature: 0.4
                });
                if (!fallback.ok) throw new Error('AI unavailable — please try again.');
                const data    = await fallback.json();
                const content = data?.choices?.[0]?.message?.content;
                return {
                    content: content ? _markIfCut(content.trim(), data.choices[0].finish_reason) : null,
                    toolCalls: null, rawMessage: null
                };
            }
            // Not failed_generation — surface the real error
            const detail = errBody?.error?.message || `(HTTP ${response.status})`;
            if (response.status === 401) throw new Error('Invalid API key. Check your API key in config.js.');
            if (response.status === 429) throw new Error('Rate limit reached. Wait a moment and try again.');
            throw new Error(`AI returned an error: ${detail}`);
        }

        if (!response.ok) {
            let detail = `(HTTP ${response.status})`;
            try { const b = await response.json(); detail = b?.error?.message || detail; } catch (_) {}
            if (response.status === 401) throw new Error('Invalid API key. Check your API key in config.js.');
            if (response.status === 429) throw new Error('Rate limit reached. Wait a moment and try again.');
            if (response.status === 503) throw new Error('AI service is temporarily unavailable. Try again shortly.');
            throw new Error(`AI returned an error: ${detail}`);
        }

        const data    = await response.json();
        const message = data?.choices?.[0]?.message;

        if (!message) return { content: null, toolCalls: null, rawMessage: null };

        const toolCalls = message.tool_calls?.length
            ? message.tool_calls.map(tc => ({
                id:   tc.id,
                name: tc.function.name,
                args: (() => {
                    try { return JSON.parse(tc.function.arguments); }
                    catch (_) { return {}; }
                })()
            }))
            : null;

        return {
            content:    message.content ? _markIfCut(message.content.trim(), data.choices[0].finish_reason) : null,
            toolCalls,
            rawMessage: message
        };

    } catch (err) {
        // timeoutId lives inside _fetchWithRetry — nothing to clear here
        throw err;
    }
}

/**
 * Multi-turn chat grounded in a note's content.
 *
 * @param {string} noteTitle   - Note title
 * @param {string} notePlain   - Note plain text (HTML stripped)
 * @param {Array}  history     - [{role, content}] previous messages (last 6 used)
 * @param {string} userMessage - The user's latest message
 * @returns {Promise<string|null>}
 */
async function chatWithNote(noteTitle, notePlain, history, userMessage) {
    const system = getMeneerSystemPrompt(
        `The student is working on a note titled: "${noteTitle}". ` +
        `Note content: ${notePlain.substring(0, 1800)}\n` +
        `Help the student understand this material. Answer questions, explain concepts, ` +
        `summarise content, and suggest study tips. Be concise and clear. ` +
        `Use plain text — no markdown headings or fences in your replies.`
    );
    const messages = [
        { role: 'system', content: system },
        ...history.slice(-8).map(m => ({ role: m.role, content: m.content })),
        { role: 'user', content: userMessage }
    ];
    return await callMeneerWithHistory(messages, 400, undefined, { prose: true });
}

/**
 * Break a task into 4-5 small, actionable steps for focus mode.
 *
 * @param {string} taskTitle       - Task title
 * @param {string} taskDescription - Task description (optional)
 * @returns {Promise<string|null>} Numbered list string, or null on failure
 */
async function chunkTask(taskTitle, taskDescription) {
    const system = getMeneerSystemPrompt(
        `Help the student break a task into small steps. ` +
        `Return EXACTLY 4-5 short action steps as a numbered list. ` +
        `Each step is one sentence. No intro, no explanation, just the list. ` +
        `Format: 1. Do this\n2. Do that`
    );
    const user = `Task: ${taskTitle}${taskDescription ? '\nDetails: ' + taskDescription : ''}`;
    return await callMeneer(system, user, 200);
}

/**
 * Generate Mermaid.js diagram syntax from a plain-English description.
 *
 * @param {string} diagramType - e.g. "flowchart", "sequenceDiagram", "classDiagram"
 * @param {string} description - User's plain-English description of the diagram
 * @returns {Promise<string|null>} Raw Mermaid syntax (no fences), or null on failure
 */
async function generateDiagram(diagramType, description) {
    const system = getMeneerSystemPrompt(
        `You are a Mermaid.js diagram code generator. ` +
        `Given a description, return ONLY valid Mermaid.js syntax for a ${diagramType}. ` +
        `No explanation, no backticks, no markdown fences, no prose. ` +
        `Start your response directly with the diagram type keyword (e.g. "flowchart TD" or "sequenceDiagram"). ` +
        `Keep node labels short (3–5 words max). Use simple, clean structure.`
    );
    const result = await callMeneer(system, description, 500);
    if (!result) return null;
    // Strip any backtick fences the model may have added despite instructions
    return result
        .replace(/^```[a-z]*\n?/i, '')
        .replace(/\n?```$/,        '')
        .trim();
}

console.log('%c🤖 Meneer loaded and ready', 'color: #3b82f6; font-weight: bold');

window.Meneer = {
    callMeneer,
    getLastCallFailure,
    describeAIFailure,
    callMeneerWithTools,
    callMeneerVision,
    suggestNoteCategory,
    generateDiagram,
    chatWithNote,
    chunkTask,
    stripHtml
};

// ── ES module surface (roadmap §3) ────────────────────────────────────────────
// window.Meneer above is an explicit assignment and survives conversion. These
// are function declarations, which do NOT become globals in a module — shim list
// from: node scripts/required-globals.js meneer.js --names
// MENEER_CONFIG is a top-level `const`. In a CLASSIC script that was a global
// lexical binding and meneer-chat.js read it bare; in a module it is module-scoped
// and simply disappears. That shipped, and broke Meneer in production with
// "MENEER_CONFIG is not defined".
//
// scripts/required-globals.js did not catch it because its `publish` set only
// collected `function` declarations and explicit window.X assignments — top-level
// const/let were invisible to it. Fixed there too.
const _meneerGlobals = {
    MENEER_CONFIG,
    _getModel, _resolvePinnedModel, callMeneer, callMeneerVision, callMeneerWithHistory,
    callMeneerWithTools, getMeneerSystemPrompt
};
export const MeneerApi = _meneerGlobals;
export { callMeneer, callMeneerWithTools, callMeneerWithHistory, getMeneerSystemPrompt, getLastCallFailure, describeAIFailure };
// Exported for tests: routing and credential choice are the two things that must
// never silently regress to a client-held key.
export { _getApiUrl, _getAuthHeader, callMeneerVision, _getModel, _getVisionModel, _providerOf, _resolvePinnedModel, MENEER_CONFIG };
if (typeof window !== 'undefined') Object.assign(window, _meneerGlobals);
